import { Component, ElementRef, OnInit } from '@angular/core';

import * as _ from 'lodash';
import { RvSortableItem, RvSortableService } from 'src/app/rv-sortable';

import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';

import { FeaturesService } from 'src/app/components/plans/features.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { FileExistenceCheckService } from '../services/file-existence-check.service';
import { FileMetadataUtilsService } from '../services/file-metadata-utils.service';
import { StockLibraryService } from '../services/stock-library.service';

interface VideoItem extends RvSortableItem {
  id: string;
  name: string;
  file?: string;
  thumbnail?: string;
  [key: string]: any;
}

@Component({
  selector: 'template-component-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  standalone: false,
})
export class VideoComponent implements OnInit {
  public componentId: string;

  uploadManager: any;
  isUploading: boolean = false;
  values = {
    volume: 0,
  };
  isDefaultFileList: boolean;
  selectedFiles: VideoItem[] = [];
  boundAddFile = this.addFile.bind(this);
  sortableService: RvSortableService<VideoItem>;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    public templateEditorService: TemplateEditorService,
    private templateEditorUtils: TemplateEditorUtilsService,
    private storageUtilsService: StorageUtilsService,
    private storageManagerService: StorageManagerService,
    private fileMetadataUtilsService: FileMetadataUtilsService,
    private fileExistenceCheckService: FileExistenceCheckService,
    public stockLibraryService: StockLibraryService,
    public currentPlanService: CurrentPlanService,
    public featuresService: FeaturesService,
  ) {
    this.componentsService.registerDirective({
      type: 'rise-video',
      element: this.elementRef.nativeElement,
      show: () => {
        this._reset();
        this.componentId = componentsService.selected.id;

        this.storageManagerService.fileType = 'video';
        this.storageManagerService.onSelectHandler = (newSelectedItems) => {
          this._addFilesToMetadata(newSelectedItems, true);
        };

        this._loadSelectedFiles();
        this._loadVolume();
      },
      getName: (componentId) => {
        var files = this._getFilesFor(componentId);

        if (files && files.length > 0) {
          return this.storageUtilsService.fileNameOf(files[0]);
        } else {
          return null;
        }
      },
    });

    this.uploadManager = {
      uploadedFiles: [],
      onUploadStatus: (isUploading) => {
        const wasUploading = this.isUploading;
        this.isUploading = isUploading;

        if (!wasUploading && isUploading) {
          this.uploadManager.uploadedFiles = [];
        }

        if (wasUploading && !isUploading) {
          const uploadedFiles = this.uploadManager.uploadedFiles;
          this.uploadManager.uploadedFiles = [];

          if (uploadedFiles.length) {
            this._addFilesToMetadata(
              this.storageUtilsService.sortFilesByName(uploadedFiles),
            );
          }
        }
      },
      addFile: (file) => {
        this.uploadManager.uploadedFiles.push(file);
      },
      isSingleFileSelector: false,
    };

    this._reset();
  }

  ngOnInit() {
    this.sortableService = new RvSortableService<VideoItem>({
      items: this.selectedFiles,
      getDisplayName: (item) => item.name || 'Video',
      onReorder: (items) => this.handleReorder(items),
      hostElement: this.elementRef,
      appendTo: '.component-container',
    });
  }

  private handleReorder(items: VideoItem[]): void {
    const currentOrder = this.selectedFiles.map((item) => item.id);

    for (let newIndex = 0; newIndex < items.length; newIndex++) {
      const item = items[newIndex];
      const oldIndex = currentOrder.indexOf(item.id);

      if (oldIndex !== newIndex) {
        this.selectedFiles.splice(
          newIndex,
          0,
          this.selectedFiles.splice(oldIndex, 1)[0],
        );
        break;
      }
    }
  }

  private ensureVideoItemsHaveIds(files: any[]): VideoItem[] {
    return files.map((file, index) => {
      const fileCopy = _.cloneDeep(file);
      if (!fileCopy.id) {
        fileCopy.id = `video-${Date.now()}-${index}`;
      }
      return fileCopy as VideoItem;
    });
  }

  _reset() {
    this._setSelectedFiles([]);
  }

  _addFilesToMetadata(files, alwaysAppend?) {
    var selectedFiles = this.isDefaultFileList ? [] : this.selectedFiles;
    var metadata = this.fileMetadataUtilsService.metadataWithFile(
      selectedFiles,
      FileMetadataUtilsService.DEFAULT_VIDEO_THUMBNAIL,
      files,
      alwaysAppend,
    );

    this._setMetadata(metadata);
  }

  _loadSelectedFiles() {
    var selectedFiles = this._getAttribute('metadata');

    if (selectedFiles) {
      this._setSelectedFiles(selectedFiles);
    }

    this.templateEditorService.loadingPresentation = true;

    this._checkFileExistenceFor(this.componentId).finally(() => {
      setTimeout(() => {
        this.templateEditorService.loadingPresentation = false;
      });
    });
  }

  _loadVolume() {
    var volume = this._getAvailableAttribute('volume');

    // default to value 0 if volume not defined
    this.values.volume = this.templateEditorUtils.intValueFor(volume, 0);
  }

  _getAttribute(key) {
    return this.attributeDataService.getAttributeData(this.componentId, key);
  }

  _setAttribute(key, value) {
    this.attributeDataService.setAttributeData(this.componentId, key, value);
  }

  _getAvailableAttribute(key) {
    return this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      key,
    );
  }

  _getBlueprintData(key) {
    return this.attributeDataService.getBlueprintData(this.componentId, key);
  }

  _getFilesFor(componentId) {
    var metadata = this.attributeDataService.getAttributeData(
      componentId,
      'metadata',
    );

    if (!metadata) {
      var files = this.attributeDataService.getAvailableAttributeData(
        componentId,
        'files',
      );

      return this.fileMetadataUtilsService.filesAttributeToArray(files);
    }

    return this.fileMetadataUtilsService.extractFileNamesFrom(metadata);
  }

  updateFileMetadata(newMetadata) {
    var currentMetadata = this._getAttribute('metadata');
    var updatedMetadata = this.fileMetadataUtilsService.getUpdatedFileMetadata(
      currentMetadata,
      newMetadata,
    );

    if (updatedMetadata) {
      this._setMetadata(updatedMetadata);
    }
  }

  _setMetadata(metadata) {
    var selectedFiles = _.cloneDeep(metadata);

    this._setSelectedFiles(selectedFiles);

    const metadataToSave =
      this.fileMetadataUtilsService.metadataForSave(metadata);
    this._setAttribute('metadata', metadataToSave);
  }

  _setSelectedFiles(selectedFiles) {
    var filesAttribute =
      this.fileMetadataUtilsService.filesAttributeFor(selectedFiles);

    this.selectedFiles = this.ensureVideoItemsHaveIds(selectedFiles);

    // Update sortable service
    if (this.sortableService) {
      this.sortableService.updateItems(this.selectedFiles);
    }

    this.isDefaultFileList = filesAttribute === this._getBlueprintData('files');
  }

  saveVolume() {
    this._setAttribute('volume', this.values.volume);
  }

  _checkFileExistenceFor(componentId) {
    var files = this._getFilesFor(componentId);

    return this.fileExistenceCheckService
      .requestMetadataFor(
        files,
        FileMetadataUtilsService.DEFAULT_VIDEO_THUMBNAIL,
      )
      .then((metadata) => {
        console.log('received metadata', metadata);

        this.updateFileMetadata(metadata);
      })
      .catch((error) => {
        console.error(
          'Could not check file existence for: ' + componentId,
          error,
        );
      });
  }

  selectFromStorage() {
    this.componentsService.editComponent({
      type: 'rise-storage-selector',
    });
  }

  removeFileFromList(file) {
    var currentMetadata = this.selectedFiles;
    var metadata = this.fileMetadataUtilsService.metadataWithFileRemoved(
      currentMetadata,
      file,
    );

    if (metadata) {
      this._setMetadata(metadata);
    }
  }

  showSettingsUI() {
    return this.selectedFiles.length > 0 && !this.isUploading;
  }

  sortItem(evt) {
    var oldIndex = evt.data.oldIndex;
    var newIndex = evt.data.newIndex;

    this.selectedFiles.splice(
      newIndex,
      0,
      this.selectedFiles.splice(oldIndex, 1)[0],
    );
  }

  addFile(file: File) {
    this._addFilesToMetadata([file]);
  }
  activateVideoUploader() {
    const uploader = document.getElementById(
      'video-list-uploader',
    ) as HTMLInputElement;
    if (uploader) {
      uploader.click();
    }
  }
}
